import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import SessionCalendar from '../components/sessions/SessionCalendar';
import {
  getPublicSessions,
  getAllViewableSessions,
  signupForSession,
  cancelSignup,
  getPlayerSignupStatus,
} from '../services/sessionManagementService';
import Button from '../components/common/Button';
import { BackButton, Input } from '../components/common';
import SessionEventRow from '../components/sessions/SessionEventRow';
import SessionFilters, {
  countActiveSessionFilters,
  SessionFilterChips,
} from '../components/sessions/SessionFilters';
import SessionDetailsModal from '../components/sessions/SessionDetailsModal';
import { Heading, Text, Typography } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import { sessionMatchesSearchQuery } from '../utils/sessionSearchUtils';
import { fetchHomeMemberships } from '../services/homeDashboardService';
import {
  homeDashboardPageRoot,
  homeDashboardColumn,
  homeSectionHeading,
  homeUpcomingSessionCardShell,
  homeElevatedInset,
} from '../components/home/homeDashboardStyles';

const UpcomingSessions = ({ currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [userSignupStatuses, setUserSignupStatuses] = useState({});
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [showCancelledSessions, setShowCancelledSessions] = useState(false);
  const [memberClubIds, setMemberClubIds] = useState(() => new Set());

  const clubFromState = location.state?.selectedClub;

  useEffect(() => {
    if (!currentUser?.id) {
      setMemberClubIds(new Set());
      return;
    }
    let cancelled = false;
    fetchHomeMemberships(currentUser.id)
      .then((rows) => {
        if (!cancelled) {
          setMemberClubIds(new Set((rows || []).map((m) => m.club_id)));
        }
      })
      .catch(() => {
        if (!cancelled) setMemberClubIds(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id]);

  useEffect(() => {
    if (clubFromState?.name) {
      setSearchQuery(clubFromState.name);
    }
  }, [clubFromState]);

  useEffect(() => {
    const loadSessionTypes = async () => {
      const { data } = await supabase.from('session_types').select('id, name').order('name');
      setSessionTypes(data || []);
    };
    loadSessionTypes();
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [filters, showCancelledSessions]);

  useEffect(() => {
    if (sessions.length > 0 && currentUser) {
      fetchUserSignupStatuses();
    }
  }, [sessions, currentUser]);

  const fetchSessions = async () => {
    setLoading(true);
    setError(null);

    try {
      const filtersWithCancelled = {
        ...filters,
        excludeCancelled: !showCancelledSessions,
      };

      let sessionsData = [];

      if (filters.visibility === 'public') {
        const publicFilters = { ...filtersWithCancelled };
        delete publicFilters.visibility;
        sessionsData = await getPublicSessions(publicFilters);
      } else {
        sessionsData = await getAllViewableSessions(currentUser.id, filtersWithCancelled);
      }

      setSessions(sessionsData);
    } catch (err) {
      console.error('Error fetching sessions:', err);
      setError('Failed to fetch sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserSignupStatuses = async () => {
    if (!currentUser) return;

    try {
      const statuses = {};
      await Promise.all(
        sessions.map(async (session) => {
          const status = await getPlayerSignupStatus(session.id, currentUser.id);
          statuses[session.id] = status;
        })
      );
      setUserSignupStatuses(statuses);
    } catch (err) {
      console.error('Error fetching user signup statuses:', err);
    }
  };

  const displayedSessions = useMemo(() => {
    if (!searchQuery.trim()) return sessions;
    return sessions.filter((session) => sessionMatchesSearchQuery(session, searchQuery));
  }, [sessions, searchQuery]);

  const activeFilterCount = countActiveSessionFilters(filters);

  const browseContext = useMemo(() => {
    if (loading) return 'Loading…';

    const total = sessions.length;
    const shown = displayedSessions.length;
    const countPart =
      searchQuery.trim() && shown !== total
        ? `${shown} of ${total} sessions`
        : `${total} session${total !== 1 ? 's' : ''}`;

    if (filters.visibility === 'public') {
      return `${countPart} · open to everyone`;
    }
    if (filters.visibility === 'private') {
      return `${countPart} · members only`;
    }
    return `${countPart} · your clubs & open sessions`;
  }, [loading, sessions.length, displayedSessions.length, searchQuery, filters.visibility]);

  const handleSignup = async (sessionId) => {
    if (!currentUser) {
      setError('Please log in to sign up for sessions');
      return;
    }

    try {
      await signupForSession(sessionId, currentUser.id);
      await fetchSessions();
      await fetchUserSignupStatuses();
    } catch (err) {
      console.error('Error signing up for session:', err);
      setError(err.message || 'Failed to sign up for session');
    }
  };

  const handleCancelSignup = async (sessionId) => {
    if (!currentUser) return;

    try {
      await cancelSignup(sessionId, currentUser.id);
      await fetchSessions();
      await fetchUserSignupStatuses();
    } catch (err) {
      console.error('Error cancelling signup:', err);
      setError(err.message || 'Failed to cancel signup');
    }
  };

  const hasSearchOrFilters =
    searchQuery.trim() ||
    activeFilterCount > 0 ||
    showCancelledSessions;

  if (!currentUser) {
    return (
      <div className={homeDashboardPageRoot}>
        <div className={`${homeDashboardColumn} py-8`}>
          <Card variant="default" size="large" className="text-center">
            <Heading level={2} className="mb-4">Please log in</Heading>
            <Text className="mb-8">You need to be logged in to find and join sessions.</Text>
            <Button onClick={() => navigate('/login')} variant="primary">
              Go to login
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className={homeDashboardPageRoot}>
      <div className={`${homeDashboardColumn} pb-16 pt-5 sm:pt-6`}>
        <header className="mb-5 max-w-2xl">
          <BackButton className="mb-3" onClick={() => navigate('/')}>
            Back to Home
          </BackButton>
          <Heading level={1} className={`mb-1 ${homeSectionHeading}`}>
            Find sessions
          </Heading>
        </header>

        <div className="mb-6 space-y-3">
          <div className="flex items-stretch overflow-hidden rounded-xl border border-border-medium bg-surface focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
            <div className="relative min-w-0 flex-1">
              <svg
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <Input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search sessions, clubs, locations…"
                className={`h-11 rounded-none border-0 bg-transparent shadow-none !pl-10 focus:!border-0 focus:!shadow-none ${searchQuery ? '!pr-10' : ''}`}
                aria-label="Search sessions"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-text-muted hover:text-text-primary"
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setFiltersExpanded((prev) => !prev)}
              aria-expanded={filtersExpanded}
              aria-controls={filtersExpanded ? 'session-filters-panel' : undefined}
              className={`inline-flex shrink-0 items-center gap-1.5 self-stretch border-l border-border-light px-3 text-sm font-medium transition-colors ${
                filtersExpanded || activeFilterCount > 0
                  ? 'bg-primary-light text-primary'
                  : 'text-text-secondary hover:bg-surface-subtle hover:text-text-primary'
              }`}
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5h18l-7 8v5l-4 2v-7L3 5z"
                />
              </svg>
              Filters
              {activeFilterCount > 0 ? (
                <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary/15 px-1 text-xs font-semibold text-primary">
                  {activeFilterCount}
                </span>
              ) : null}
            </button>
          </div>

          {filtersExpanded ? (
            <div
              id="session-filters-panel"
              className="rounded-xl border border-border-light bg-surface-subtle/70 p-3 sm:p-4"
            >
              <SessionFilters
                filters={filters}
                onFiltersChange={setFilters}
                expanded
                showCancelledSessions={showCancelledSessions}
                onShowCancelledChange={setShowCancelledSessions}
              />
            </div>
          ) : (
            <SessionFilterChips
              filters={filters}
              sessionTypes={sessionTypes}
              onFiltersChange={setFilters}
            />
          )}

          <Typography variant="caption" className="block text-text-muted">
            {browseContext}
          </Typography>
        </div>

        {error && (
          <div className={`${homeElevatedInset} px-4 py-3 mb-6 flex flex-wrap items-center justify-between gap-3`}>
            <Typography variant="body-small" className="text-error">
              {error}
            </Typography>
            <Button type="button" variant="outline" size="small" onClick={() => fetchSessions()}>
              Retry
            </Button>
          </div>
        )}

        {loading && (
          <div className={`text-center py-16 px-6 ${homeUpcomingSessionCardShell}`}>
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent mx-auto mb-4" />
            <Typography variant="muted">Loading sessions…</Typography>
          </div>
        )}

        {!loading && displayedSessions.length === 0 && (
          <div className={`text-center py-14 px-6 ${homeUpcomingSessionCardShell}`}>
            <div className="text-4xl mb-4">🏸</div>
            <Heading level={3} className="mb-2 text-base sm:text-lg">
              {sessions.length === 0 ? 'No sessions found' : 'No matches'}
            </Heading>
            <Text className="text-text-secondary text-sm max-w-md mx-auto">
              {sessions.length === 0
                ? 'Nothing upcoming matches your filters right now.'
                : `No sessions match "${searchQuery.trim()}". Try fewer words or clear your search.`}
            </Text>
            {hasSearchOrFilters && (
              <div className="flex flex-wrap justify-center gap-2 mt-6">
                {searchQuery.trim() && (
                  <Button variant="outline" size="small" onClick={() => setSearchQuery('')}>
                    Clear search
                  </Button>
                )}
                {activeFilterCount > 0 && (
                  <Button variant="outline" size="small" onClick={() => setFilters({})}>
                    Clear filters
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {!loading && displayedSessions.length > 0 && (
          <SessionCalendar
            layout="timeline"
            sessions={displayedSessions}
            sessionCardComponent={SessionEventRow}
            sessionCardProps={{
              currentUser,
              memberClubIds,
              userSignupStatus: userSignupStatuses,
              onSignup: handleSignup,
              onCancelSignup: handleCancelSignup,
              onViewDetails: (sessionId) => {
                setSelectedSessionId(sessionId);
                setShowDetailsModal(true);
              },
            }}
            showDateHeaders={true}
            showSessionCount={true}
          />
        )}

        {showDetailsModal && selectedSessionId && (
          <SessionDetailsModal
            sessionId={selectedSessionId}
            isOpen={showDetailsModal}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedSessionId(null);
            }}
            onSignup={handleSignup}
            onCancelSignup={handleCancelSignup}
            currentUser={currentUser}
            userSignupStatus={userSignupStatuses[selectedSessionId]}
          />
        )}
      </div>
    </div>
  );
};

export default UpcomingSessions;
